

import { Check, Plus } from "lucide-react";
import { useWatch } from "react-hook-form";
import { WEIGHT_UNIT_TYPES_OPTIONs } from "../../../config/optionsConstants";
import { CustomButton } from "../../ui/button/CustomButton/CustomButton";
import { Button } from "../../ui/common/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/common/tooltip";
import { FormInput } from "../../ui/Input/FormInput/FormInput";
import { FormInputNumber } from "../../ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../ui/Input/FormSelector/FormSelect";
import { FormUnitInput } from "../../ui/Input/FormUnitInput/FormUnitInput";
import {
  WASTE_MANAGEMENT_TYPE_IDS,
  WASTE_VALIDATION,
  WASTE_VALIDATION_MESSAGES
} from "../wasteManagement.constants";
import {
  MOISTURE_CONTENT_RULES,
  WASTE_STREAM_NAME_RULES
} from "../wasteManagement.validation";

interface WasteRecordFormProps {
  control: any;
  globalIndex: number;
  field: any;
  isDraft: boolean;
  managementTypeOptions: Array<{ value: string; text: string; label: string }>;
  ingredientOptions: Array<{ value: string; text: string; label: string }>;
  disabled: boolean;
  lockStatus: boolean;
  onSave: (index: number) => Promise<boolean>;
  onCancel: () => void;
  onAddRecord: () => void;
}

/**
 * Expandable form for creating (isDraft=true) or editing (isDraft=false) a
 * single waste management record.
 *
 * The form renders a 3-column grid with: Management Name, Management Type,
 * and Distribution (with unit). For the "Recovered for use or sale" management
 * type (id "1"), two additional fields appear: Ingredient Name and Moisture %.
 */
export const WasteRecordForm = ({
  control,
  globalIndex,
  field,
  isDraft,
  managementTypeOptions,
  ingredientOptions,
  disabled,
  lockStatus,
  onSave,
  onCancel,
  onAddRecord
}: WasteRecordFormProps) => {
  const fieldPrefix = `wastes.${globalIndex}`;

  /**
   * Watch only this record's managementTypeId to conditionally render the
   * "Recovered for use or sale" extra fields.
   * reading from the parent's wastes[]
   * array via prop would create a tight coupling (the parent would need to
   * pass recordData just for this one conditional). Using useWatch here
   * keeps the component self-contained and avoids prop bloat.
   */
  const managementTypeId = useWatch({
    control,
    name: `${fieldPrefix}.managementTypeId` as any
  });

  return (
    <div
      className={`tw-border tw-rounded-md tw-p-3 tw-space-y-2 ${
        isDraft ? "tw-border-dashed tw-border-primary/30 tw-bg-primary/5" : "tw-border-primary/30 tw-bg-primary/5"
      }`}
    >
      {/* Form header */}
      <div className="tw-flex tw-items-center tw-justify-between">
        <span className="tw-text-xs tw-font-semibold tw-text-foreground">
          {isDraft ? "New Waste Record" : `Waste ${globalIndex + 1}`}
        </span>
      </div>

      {/* 3-column main fields */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-2">
        <div className="tw-space-y-1">
          <label className="tw-text-[10px] tw-text-muted-foreground">
            Management Name<span className="tw-text-destructive">*</span>
          </label>
          <FormInput
            control={control}
            name={`${fieldPrefix}.managementName` as any}
            placeholder="e.g., Whey stream"
            required={true}
            errorMessage={WASTE_VALIDATION_MESSAGES.STREAM_NAME_REQUIRED}
            disabled={disabled || lockStatus}
            rules={WASTE_STREAM_NAME_RULES}
          />
        </div>

        <div className="tw-space-y-1">
          <label className="tw-text-[10px] tw-text-muted-foreground">
            Management Type<span className="tw-text-destructive">*</span>
          </label>
          <FormSelect
            control={control}
            name={`${fieldPrefix}.managementTypeId` as any}
            placeholder="Select management"
            options={managementTypeOptions}
            disabled={disabled || lockStatus}
            required={true}
          />
        </div>

        <div className="tw-space-y-1">
          <label className="tw-text-[10px] tw-text-muted-foreground">Distribution</label>
          
          <FormUnitInput
            control={control}
            name={`${fieldPrefix}.sideStreamsDistribution` as any}
            placeholder="0"
            required={true}
            errorMessage={WASTE_VALIDATION_MESSAGES.DISTRIBUTION_REQUIRED}
            disabled={disabled || lockStatus}
            unitOptions={WEIGHT_UNIT_TYPES_OPTIONs}
            unitName={`${fieldPrefix}.unit` as any}
            min={{
              value: WASTE_VALIDATION.MIN_DISTRIBUTION_VALUE,
              message: "Distribution must be greater than 0"
            }}
          />
        </div>
      </div>

      
      {managementTypeId === WASTE_MANAGEMENT_TYPE_IDS.RECOVERED_FOR_USE_OR_SALE && (
        <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-pt-1 tw-border-t tw-border-border/50">
          <div className="tw-space-y-1">
            <label className="tw-text-[10px] tw-text-muted-foreground">Ingredient Name</label>
            {ingredientOptions.length > 0 ? (
              <FormSelect
                control={control}
                name={`${fieldPrefix}.ingredientId` as any}
                placeholder="Select ingredient"
                options={ingredientOptions}
                disabled={lockStatus}
                required={false}
              />
            ) : (
              <FormInput
                control={control}
                name={`${fieldPrefix}.ingredientId` as any}
                placeholder="No ingredients defined yet"
                disabled={true}
                required={false}
              />
            )}
          </div>

          <div className="tw-space-y-1">
            <label className="tw-text-[10px] tw-text-muted-foreground">Moisture %</label>
            <FormInputNumber
              control={control}
              name={`${fieldPrefix}.moistureContent` as any}
              label=""
              placeholder="0"
              disabled={disabled || lockStatus}
              rules={MOISTURE_CONTENT_RULES}
            />
          </div>
        </div>
      )}


      <div className="tw-flex tw-justify-end tw-pt-1">
        {/* EDIT MODE: Cancel + Confirm buttons */}
        {!isDraft && (
          <div className="tw-flex tw-items-center tw-gap-2">
            <CustomButton
              onClick={onCancel}
              iconSize="small"
              className="!tw-h-8 tw-rounded-md tw-gap-1.5 tw-px-3 has-[>svg]:tw-px-2.5 !tw-bg-transparent tw-border tw-border-border tw-text-foreground hover:tw-bg-muted/50"
              title="Cancel"
              theme="lightgrey"
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    className="tw-h-7 tw-gap-1 tw-text-xs"
                    onClick={async () => {
                      const saved = await onSave(globalIndex);
                      if (saved) onCancel();
                    }}
                    disabled={disabled || lockStatus}
                  >
                    <Check className="tw-h-3.5 tw-w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="tw-text-xs">Update changes</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}

        {/* DRAFT MODE: Save Record button */}
        {isDraft && (
          <Button
            size="sm"
            className="tw-gap-1 tw-text-xs tw-h-7"
            onClick={onAddRecord}
            disabled={disabled || lockStatus}
          >
            <Plus className="tw-h-3 tw-w-3" /> Save Record
          </Button>
        )}
      </div>
    </div>
  );
};
